import React, { useEffect, useMemo, useState } from 'react';
import { Tree, TreeNode } from 'react-organizational-chart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faPlus, faBan } from '@fortawesome/free-solid-svg-icons'
import { CollectionReptile, FamilyTreeFamily } from '../collection/types/CollectionTypes.ts';
import { OpenCreateModalInfo } from '../collection/modals/FamilyTreeEditModal.tsx';
import { TreeIndividual } from '../collection/types/CollectionTypes.ts';
import { BasicFetch } from '../../utils/BasicFetch.ts';
import { Button, Table } from 'react-bootstrap';
import { GeneText } from '../utilities/GeneText.tsx';

const PHOTO_NOT_AVAILABLE = "images/notavailable.jpg";

const findIndividual = (family: FamilyTreeFamily, id: number): TreeIndividual | null => {
  if (family.individual.id === id) {
    return family.individual;
  }
  if (family.father_family) {
    const father = findIndividual(family.father_family, id);
    if (father) {
      return father;
    }
  }
  if (family.mother_family) {
    const mother = findIndividual(family.mother_family, id);
    if (mother) {
      return mother;
    }
  }
  return null;
}

const FamilyTreePopup = ({
  reptileId,
  indiv,
  saveDetails,
  closeSelf,
  isSelf,
  setEditingFamilyIndividual,
  readOnly
}: {
  reptileId: number,
  indiv: TreeIndividual,
  saveDetails: (reptile: CollectionReptile) => void,
  closeSelf: () => void,
  isSelf: boolean,
  setEditingFamilyIndividual: (indiv: TreeIndividual) => void,
  parentType?: 'father' | 'mother',
  readOnly: boolean,

}) => {

  const removeIndiv = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/tree/${reptileId}/family/${indiv.family_id}/delete/${indiv.sex}`,
      method: 'DELETE',
      data: {},
      onSuccess: ({ reptile }) => {
        saveDetails(reptile);
        closeSelf();
      },
      onFail: () => {
        alert('failed to delete');
      }
    })
  }

  const displayPicture = useMemo(() => {
    return indiv.picture.url || indiv.img || PHOTO_NOT_AVAILABLE;
  }, [indiv])

  return (
    <div id='family-shadow' className='shadow' onClick={(event) => {
      if (event.target === event.currentTarget) {
        closeSelf();
      }
    }}>
      <div id="details-popup" className='d-flex flex-column text-left bg-dark' >
        <div className="d-flex flex-column no-pad">
          <div style={{
            backgroundImage: `url(${displayPicture})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            height: '200px',
            borderRadius: '10px'
          }} />
          {indiv.picture.credit && <span>{indiv.picture.credit}</span>}
        </div>
        {!readOnly && <div className='my-2 d-flex'>
          <Button size='sm' className='mx-auto' onClick={() => setEditingFamilyIndividual(indiv)} variant='primary'>
            Edit Details
          </Button>
        </div>}
        <div>
          <Table size='sm' variant='dark'>
            <tbody>
              <tr>
                <td style={{ fontWeight: 'normal' }}>Name</td>
                <td>{indiv.name}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'normal' }}>Breeder</td>
                <td>{indiv.breeder}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 'normal' }}>Morph</td>
                <td>{indiv.morph}</td>
              </tr>
              {indiv.genes && !!indiv.genes.length && <tr>
                <td style={{ fontWeight: 'normal' }}>Genes</td>
                <td>
                  {indiv.genes.map((gene) => {
                    return <div><GeneText gene={gene} key={gene.id} small={false} /></div>
                  })}
                </td>
              </tr>}
            </tbody>
          </Table>
        </div>
        <div className="d-flex flex-row mt-2 justify-content-between">
          <Button onClick={closeSelf} size='sm' variant='outline-light' className=' py-2 px-4'>
            Close
          </Button>
          {!readOnly && !isSelf && <Button id="tree-trash-button" onClick={removeIndiv} size='sm' variant='outline-light' className="py-2 px-4">
            <FontAwesomeIcon icon={faTrash} />
          </Button>}
        </div>
      </div>
    </div >

  )
}


const ReptileTreeNode = ({
  reptile,
  gender,
  setEditingIndiv,
  setCreatingFamilyInfo,
  readOnly
}: {
  reptile: TreeIndividual,
  gender: string,
  setEditingIndiv: (indiv: TreeIndividual) => void,
  setCreatingFamilyInfo: () => void,
  readOnly: boolean
}) => {
  const blank = !reptile || (!reptile.morph && !reptile.name);

  return (
    <>
      {!blank && <div className={`reptile-tile ${gender}`} onClick={() => setEditingIndiv(reptile)}>
        <div className='d-flex flex-column'>
          <span className='text-truncate'>{reptile.name || reptile.morph}</span>
        </div>
        <img className="w-100" src={reptile.picture.url || PHOTO_NOT_AVAILABLE} alt="" />
        {reptile.picture.credit && <h6 className="tile-photo-credit">{reptile.picture.credit}</h6>}
      </div>}
      {blank && <div onClick={setCreatingFamilyInfo}
        className={`reptile-tile ${gender}`} >
        {!readOnly ? <FontAwesomeIcon size='3x' icon={faPlus} /> : <FontAwesomeIcon size='3x' icon={faBan} />}
      </div>
      }
    </>
  )
}



const TreeFamilyNode = ({
  family,
  childFamilyId,
  sex,
  childCollectionIndivId,
  setReptile,
  setEditingIndiv,
  setCreatingFamilyInfo,
  readOnly,
}: {
  family: FamilyTreeFamily,
  sex: 'male' | 'female',
  childFamilyId: number,
  childCollectionIndivId: number,
  setReptile: (reptile: CollectionReptile) => void,
  setEditingIndiv: (indiv: TreeIndividual) => void,
  setCreatingFamilyInfo: (info: OpenCreateModalInfo) => void,
  readOnly: boolean,
}) => {

  const blank = !family.individual || (!family.individual.morph && !family.individual.name);
  const showParent = !blank || !readOnly;

  return (
    <TreeNode label={
      <ReptileTreeNode
        reptile={family.individual}
        gender={sex}
        setEditingIndiv={(indiv) => setEditingIndiv({ ...indiv, familyId: childFamilyId, sex })}
        setCreatingFamilyInfo={() => setCreatingFamilyInfo({ childCollectionIndivId, sex })}
        readOnly={readOnly}
      />
    }>
      {family.father_family && showParent && <TreeFamilyNode
        family={family.father_family}
        childFamilyId={family.family_id}
        sex="male"
        setReptile={setReptile}
        setEditingIndiv={setEditingIndiv}
        setCreatingFamilyInfo={setCreatingFamilyInfo}
        readOnly={readOnly}
        childCollectionIndivId={family.individual.id}
      />}
      {family.mother_family && showParent && <TreeFamilyNode
        family={family.mother_family}
        childFamilyId={family.family_id}
        sex="female"
        setReptile={setReptile}
        setEditingIndiv={setEditingIndiv}
        setCreatingFamilyInfo={setCreatingFamilyInfo}
        readOnly={readOnly}
        childCollectionIndivId={family.individual.id}
      />}
    </TreeNode>
  )
}



export const ReptoolFamilyTree = ({
  reptile,
  setReptile,
  setCreatingFamilyInfo,
  setEditingFamilyIndividual,
  readOnly
}: {
  reptile: CollectionReptile,
  setReptile: (reptile: CollectionReptile) => void,
  setCreatingFamilyInfo: (info: OpenCreateModalInfo) => void,
  setEditingFamilyIndividual: (indiv: TreeIndividual | null) => void,
  readOnly: boolean
}) => {
  const treeData = useMemo(() => reptile.treeData, [reptile])
  const [editingIndiv, setEditingIndiv] = useState<TreeIndividual | null>(null);
  const createTree = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/collection_reptile/${reptile.id}/create_tree`,
      method: 'POST',
      data: {},
      onSuccess: ({ reptile }) => {
        setReptile(reptile);
      },
      onFail: () => {
        alert('failed to create tree');
      }
    })
  }
  useEffect(() => {
    if (editingIndiv) {
      const currentIndiv = findIndividual(treeData, editingIndiv.id);
      setEditingFamilyIndividual(currentIndiv);
      setEditingIndiv(currentIndiv);
    } else {
      setEditingFamilyIndividual(null);
      setEditingIndiv(null);

    }
  }, [reptile])
  return (
    <>
      {editingIndiv && <FamilyTreePopup
        reptileId={reptile.id}
        indiv={editingIndiv}
        saveDetails={setReptile}
        closeSelf={() => setEditingIndiv(null)}
        isSelf={editingIndiv.indiv_id === reptile.id}
        readOnly={readOnly}
        setEditingFamilyIndividual={setEditingFamilyIndividual}
      />}
      <Tree
        lineWidth={'3px'}
        lineColor={'#2bc3c3'}
        lineBorderRadius={'10px'}
        label={<ReptileTreeNode
          reptile={treeData.individual}
          gender={reptile.gender}
          setEditingIndiv={setEditingIndiv}
          setCreatingFamilyInfo={() => setCreatingFamilyInfo({ sex: reptile.gender, childCollectionIndivId: treeData.individual.id })}
          readOnly={readOnly}
        />}
      >
        {!reptile.has_tree && <Button onClick={createTree} style={{ transform: 'rotate(180deg)' }} className="button mx-auto py-2 px-4">Create Tree</Button>}
        {treeData.father_family && <TreeFamilyNode
          family={treeData.father_family}
          childFamilyId={treeData.family_id}
          sex={"male"}
          setReptile={setReptile}
          setEditingIndiv={setEditingIndiv}
          setCreatingFamilyInfo={setCreatingFamilyInfo}
          readOnly={readOnly}
          childCollectionIndivId={treeData.individual.id}
        />}
        {treeData.mother_family && <TreeFamilyNode
          family={treeData.mother_family}
          childFamilyId={treeData.family_id}
          sex={"female"}
          setReptile={setReptile}
          setEditingIndiv={setEditingIndiv}
          setCreatingFamilyInfo={setCreatingFamilyInfo}
          readOnly={readOnly}
          childCollectionIndivId={treeData.individual.id}
        />}
      </Tree>
    </>

  )
}
